import { readScorerSquad } from '../helpers/scorerSquads';
import i18next from "i18next";
import {
  Avatar,
  Box,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { fetchFromApi } from "../helpers/fetchFromApi";
import { tipsRequest } from "../helpers/tipsWorkflow";
import {
  normalizeSquadPlayer,
  playerAge,
  playerPosition,
  sortSquad,
  squadNumber,
  POSITION_OPTIONS,
} from "../helpers/squadPlayers";
import "./FixtureSquads.css";
export { normalizeSquadPlayer, playerAge };
export const normalizeFixtureLineups = (fixture) =>
  (fixture?.lineups || fixture?.data?.lineups || []).map((e) =>
    normalizeSquadPlayer(e)
  );
const readJson = async (path) => {
  const response = await fetchFromApi(path),
    payload = await response.json();
  if (
    !response.ok ||
    !Object.prototype.hasOwnProperty.call(payload || {}, "data")
  )
    throw Error("Kaderdaten konnten nicht geladen werden.");
  return payload.data;
};
export async function loadFixtureSquads({
  seasonId,
  fixtureId,
  homeTeamId,
  awayTeamId,
}) {
  const results = await Promise.allSettled([
    readScorerSquad(seasonId,homeTeamId),
    readScorerSquad(seasonId,awayTeamId),
    readJson(
      `fetch-data-v3/fixtures/${fixtureId}?include=lineups.player;lineups.position;lineups.player.nationality;lineups.player.detailedPosition`
    ),
  ]);
  const [home, away, fixture] = results.map(result => result.status === "fulfilled" ? result.value : null);
  if (results.every(result => result.status === "rejected")) throw Error("Kaderdaten konnten nicht geladen werden.");
  if (fixture && (
    Number(fixture?.season_id) !== Number(seasonId) ||
    !Number.isSafeInteger(Number(fixture?.league_id))
  ))
    throw Error("Saison der Paarung passt nicht zum Kader.");
  const squadLeagueIds = [...new Set([home,away].flatMap(rows => Array.isArray(rows) ? rows : [])
    .filter(row => Number(row.season_id) === Number(seasonId) && Number(row.season?.id) === Number(seasonId) && [Number(homeTeamId),Number(awayTeamId)].includes(Number(row.team_id)))
    .map(row => Number(row.season.league_id)).filter(id => Number.isSafeInteger(id) && id > 0))];
  const leagueId = fixture?.league_id ?? (squadLeagueIds.length === 1 ? squadLeagueIds[0] : null);
  const squads = {},
    teamMetadata = {};
  for (const [side, raw, teamId] of [
    ["home", home, homeTeamId],
    ["away", away, awayTeamId],
  ]) {
    const rows = (Array.isArray(raw) ? raw : []).filter(
      (e) =>
        Number(e.team_id) === Number(teamId) &&
        Number(e.season_id) === Number(seasonId)
    );
    // Reject statistics from conflicting competitions even if a provider response is malformed.
    squads[side] = rows.map((e) =>
      normalizeSquadPlayer(e, { seasonId, teamId, leagueId })
    );
    teamMetadata[side] =
      rows.find((e) => Number(e.team?.id) === Number(teamId))?.team || {};
  }
  const all = [...squads.home, ...squads.away];
  const lineups = normalizeFixtureLineups(fixture).map((p) => ({
    ...p,
    ...all.find((s) => s.id === p.id && s.teamId === p.teamId),
    number:
      p.number ??
      all.find((s) => s.id === p.id && s.teamId === p.teamId)?.number,
  }));
  return {
    squads,
    lineups,
    unavailable: results.map((result,index) => result.status === "rejected" ? ["home","away","lineups"][index] : null).filter(Boolean),
    lineupConfirmed: fixture?.lineups_confirmed === true,

    teamMetadata,
    leagueId,
    seasonId,
  };
}
export async function loadPositionOverrides(ids) {
  return (
    await tipsRequest("player-position-overrides", {
      mode: "load",
      player_ids: ids,
    })
  ).overrides;
}
export async function savePositionOverride(playerId, position) {
  return tipsRequest("player-position-overrides", {
    mode: "save",
    player_id: playerId,
    position,
  });
}
function PlayerList({ players, team, overrides, onProfile }) {
  const ordered = useMemo(
    () => sortSquad(players, overrides),
    [players, overrides]
  );
  return (
    <section className="squad-team" data-testid={`player-list-${team.side}`}>
      <header className="squad-team-heading">
        <Avatar
          src={team.image_path || team.imagePath || undefined}
          alt=""
          sx={{ width: 32, height: 32 }}
        />
        <h3>{team.name || team.custom_name || "–"}</h3>
      </header>
      <div
        className="squad-scroll"
        tabIndex={0}
        role="region"
        aria-label={`Kader ${team.name}`}
      >
        <table className="squad-table">
          <thead>
            <tr>
              {[
                "#",
                "Spieler",
                "POS",
                "Alter",
                "NAT",
                "SP (Ø Min.)",
                "T",
                "A",
                "Note",
                "GK",
                "RK",
              ].map((h) => (
                <th
                  key={h}
                  scope="col"
                  title={
                    {
                      SP: "Einsätze",
                      GK: "Gelbe Karten",
                      RK: "Direkte rote Karten (ohne Gelb-Rot)",
                      Note: "Durchschnitt gültiger Sportmonks-Bewertungen",
                      "SP (Ø Min.)":
                        "Einsätze und durchschnittliche Minuten pro Einsatz in dieser Liga/Saison",
                    }[h]
                  }
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ordered.map((p, i) => {
              const pos = playerPosition(p, overrides[p.id]),
                s = p.statistics || {};
              return (
                <tr
                  key={Number.isFinite(p.id) ? p.id : i}
                  data-testid="squad-player"
                  className={`squad-group-${pos.group}`}
                >
                  <td>{squadNumber(p.number)}</td>
                  <td className="squad-name">
                    <button
                      className="squad-player-button"
                      aria-label={p.name}
                      onClick={() => onProfile(p)}
                      title={p.fullName || p.name}
                    >
                      <Avatar
                        src={p.image || undefined}
                        alt=""
                        sx={{ width: 27, height: 27, bgcolor: "#435160" }}
                      >
                        <span aria-label="Kein Spielerfoto">●</span>
                      </Avatar>
                      <strong>{p.name}</strong>
                      {s.captainGames > 0 && (
                        <span
                          title={`Kapitän in ${s.captainGames} Spielen dieser Liga/Saison`}
                        >
                          (C)
                        </span>
                      )}
                    </button>
                  </td>
                  <td
                    data-testid="squad-position"
                    style={{ textAlign: "center" }}
                  >
                    <span
                      className={`squad-position${
                        pos.manual ? " squad-manual" : ""
                      }`}
                      title={
                        pos.manual
                          ? "Manuelle Position für dein Profil"
                          : pos.fallback
                          ? "Genaue Position fehlt; nur Provider-Positionsgruppe"
                          : p.detailedPosition?.name || "Torhüter"
                      }
                    >
                      {pos.label}
                      {pos.manual && <sup>*</sup>}
                    </span>
                  </td>
                  <td title={p.dateOfBirth || ""}>
                    {squadNumber(playerAge(p.dateOfBirth))}
                  </td>
                  <td>
                    {p.flag && p.nationality ? (
                      <img
                        className="squad-flag"
                        src={p.flag}
                        alt={p.nationality}
                        aria-label={p.nationality}
                        title={p.nationality}
                      />
                    ) : (
                      <span
                        title={p.nationality || "Nationalität fehlt"}
                        aria-label={p.nationality || "Nationalität fehlt"}
                      >
                        –
                      </span>
                    )}
                  </td>
                  <td>
                    {squadNumber(s.appearances)} (
                    {squadNumber(s.averageMinutes)})
                  </td>
                  <td>{squadNumber(s.goals)}</td>
                  <td>{squadNumber(s.assists)}</td>
                  <td>{squadNumber(s.rating, 2)}</td>
                  <td>{squadNumber(s.yellow)}</td>
                  <td>{squadNumber(s.red)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!players.length && <p>Keine Daten verfügbar.</p>}
    </section>
  );
}
export function FixtureSquads({
  open,
  seasonId,
  fixtureId,
  teams,
  loader = loadFixtureSquads,
  overrideLoader = loadPositionOverrides,
  overrideSaver = savePositionOverride,
}) {
  const [state, setState] = useState({
      status: "idle",
      squads: { home: [], away: [] },
      lineups: [],
    }),
    [view, setView] = useState("squad"),
    [overrides, setOverrides] = useState({}),
    [overrideError, setOverrideError] = useState(""),
    [selected, setSelected] = useState(null),
    [draft, setDraft] = useState(""),
    [busy, setBusy] = useState(false),
    [saveError, setSaveError] = useState("");
  const homeTeamId = teams[0].id,
    awayTeamId = teams[1].id;
  useEffect(() => {
    let cancelled = false;
    if (!open) return undefined;
    setState({
      status: "loading",
      squads: { home: [], away: [] },
      lineups: [],
    });
    setSelected(null);
    setView("squad");
    setOverrides({});
    setOverrideError("");
    loader({ seasonId, fixtureId, homeTeamId, awayTeamId })
      .then(async (data) => {
        if (cancelled) return;
        setState({ status: "ready", ...data });
        try {
          const ids = [
            ...new Set(
              [...data.squads.home, ...data.squads.away, ...data.lineups]
                .map((p) => p.id)
                .filter(Number.isSafeInteger)
            ),
          ];
          const result = await overrideLoader(ids);
          if (!cancelled) setOverrides(result || {});
        } catch {
          if (!cancelled)
            setOverrideError(
              "Manuelle Positionen konnten nicht geladen werden. Sportmonks-Positionen werden angezeigt."
            );
        }
      })
      .catch(() => {
        if (!cancelled)
          setState({
            status: "error",
            squads: { home: [], away: [] },
            lineups: [],
          });
      });
    return () => {
      cancelled = true;
    };
  }, [
    open,
    seasonId,
    fixtureId,
    homeTeamId,
    awayTeamId,
    loader,
    overrideLoader,
  ]);
  const lineups = useMemo(
    () => ({
      home: state.lineups.filter((p) => p.teamId === Number(homeTeamId)),
      away: state.lineups.filter((p) => p.teamId === Number(awayTeamId)),
    }),
    [state.lineups, homeTeamId, awayTeamId]
  );
  if (!open || state.status === "idle") return null;
  if (state.status === "loading")
    return (
      <Stack direction="row" gap={1}>
        <CircularProgress size={18} />
        <Typography>Kader werden geladen …</Typography>
      </Stack>
    );
  if (state.status === "error")
    return (
      <Typography color="error.light">
        Kaderdaten sind derzeit nicht verfügbar. Der übrige Arbeitsbereich
        bleibt unverändert nutzbar.
      </Typography>
    );
  const hasLineup = lineups.home.length > 0 || lineups.away.length > 0,
    displayed = view === "lineup" ? lineups : state.squads;
  const select = (p) => {
    setSelected(p);
    setDraft(overrides[p.id] || "");
    setSaveError("");
  };
  const save = async () => {
    setBusy(true);
    setSaveError("");
    try {
      const result = await overrideSaver(selected.id, draft || null);
      if (
        result?.success !== true ||
        result.player_id !== selected.id ||
        result.position !== (draft || null)
      )
        throw Error("SAVE_FAILED");
      setOverrides((current) => {
        const next = { ...current };
        if (draft) next[selected.id] = draft;
        else delete next[selected.id];
        return next;
      });
      setSelected(null);
    } catch {
      setSaveError("Position wurde nicht bestätigt. Bitte erneut versuchen.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box data-testid="fixture-squads" className="fixture-squads">
      {hasLineup ? (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={view}
          onChange={(_, v) => v && setView(v)}
        >
          <ToggleButton value="squad">Kader</ToggleButton>
          <ToggleButton value="lineup">Aufstellung</ToggleButton>
        </ToggleButtonGroup>
      ) : (
        <small>{i18next.language?.startsWith("en") ? "No lineup available yet." : "Noch keine Aufstellung verfügbar."}</small>
      )}
      {state.unavailable?.length > 0 && <p role="status">{i18next.language?.startsWith("en")
        ? "Some squad/lineup requests failed. Successfully loaded data remains visible."
        : "Einzelne Kader-/Aufstellungsabfragen fehlgeschlagen. Erfolgreich geladene Daten bleiben sichtbar."}</p>}
      {hasLineup && <small>{state.lineupConfirmed
        ? (i18next.language?.startsWith("en") ? "Confirmed lineup" : "Bestätigte Aufstellung")
        : (i18next.language?.startsWith("en") ? "Available lineup; confirmation not provided" : "Verfügbare Aufstellung; Bestätigung nicht übermittelt")}</small>}
      <p className="squad-hint">
        Statistiken ausschließlich für die gewählte Liga und Saison. – = nicht
        vorhanden. DEF/MID/ATT = genaue Position fehlt. (C) = als Kapitän
        eingesetzt.
      </p>
      {overrideError && <p role="alert">{overrideError}</p>}
      <div className="squad-teams">
        {teams.map((team) => (
          <PlayerList
            key={team.side}
            team={{
              ...team,
              ...state.teamMetadata?.[team.side],
              side: team.side,
            }}
            players={displayed[team.side] || []}
            overrides={overrides}
            onProfile={select}
          />
        ))}
      </div>
      <Dialog
        open={!!selected}
        onClose={() => !busy && setSelected(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { bgcolor: "#172737", color: "#edf3fa" } }}
      >
        <DialogTitle>{selected?.fullName || selected?.name}</DialogTitle>
        <DialogContent>
          <p>
            Größe: {squadNumber(selected?.height)} cm · Gewicht:{" "}
            {squadNumber(selected?.weight)} kg
          </p>
          <p>
            Nationalität: {selected?.nationality || "–"} · Geburtsdatum:{" "}
            {selected?.dateOfBirth || "–"}
          </p>
          <p>
            Sportmonks-Position:{" "}
            {selected?.detailedPosition?.name || selected?.position || "–"}
          </p>
          <label className="squad-position-editor">
            Manuelle Position (nur dein Profil)
            <select
              aria-label="Manuelle Position"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={busy || !!overrideError}
            >
              <option value="">Sportmonks verwenden</option>
              {POSITION_OPTIONS.map((p) => (
                <option key={p}>{p}</option>
              ))}
            </select>
          </label>
          <p>
            Die Korrektur gilt für diesen Spieler auch nach einem Saisonwechsel.
            Sportmonks-Daten bleiben unverändert.
          </p>
          {saveError && <p role="alert">{saveError}</p>}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setSelected(null)}>
            Schließen
          </Button>
          <Button disabled={busy || !!overrideError} onClick={save}>
            Position speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
